import React, { Fragment, useEffect, useState, useRef } from "react";
import { Row, Col, Card, Button, Form, Modal } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import PageTitle from "../../layouts/PageTitle";
import SignatureCanvas from "react-signature-canvas";
import swal from "sweetalert";
import api from "../../../api";

const MomAdd = () => {
 const navigate = useNavigate();
 const [searchParams] = useSearchParams();
 const workOrderFromUrl = searchParams.get("workOrder") || "";

 const [loading, setLoading] = useState(false);

 const [formData, setFormData] = useState({
 meetingDate: "",
 preparedBy: "",
 duration: "",
 fromTime: "",
 toTime: "",
 venue: "",
 objective: "",
 companyName: "",
 clientName: "",
 });

 const [minutes, setMinutes] = useState([""]);
 const [hostParticipants, setHostParticipants] = useState([""]);
 const [clientParticipants, setClientParticipants] = useState([""]);

 const [projects, setProjects] = useState([]);
 const [workOrders, setWorkOrders] = useState([]);
 const [filteredWorkOrders, setFilteredWorkOrders] = useState([]);

 const [selectedProject, setSelectedProject] = useState("");
 const [selectedWorkOrder, setSelectedWorkOrder] = useState("");

 // ================= SIGNATURE =================
 const clientSigRef = useRef(null);
 const [showClientModal, setShowClientModal] = useState(false);
 const [clientSignature, setClientSignature] = useState(null);

 // ================= LOAD DATA =================
 useEffect(() => {
 fetchProjects();
 fetchWorkOrders();
 }, []);

 const fetchProjects = async () => {
 const res = await api.get("/projects?limit=1000");
 if (res.data.success) setProjects(res.data.data);
 };

 const fetchWorkOrders = async () => {
 const res = await api.get("/work-orders?limit=1000");

 if (res.data.success) {
 const allWorkOrders = res.data.data;
 setWorkOrders(allWorkOrders);

 // Auto select work order if coming from filter
      if (workOrderFromUrl) {
        const wo = allWorkOrders.find((w) => String(w._id) === String(workOrderFromUrl));

        if (wo) {
          const projectId = wo.project?._id || wo.project;

          setSelectedProject(projectId);
          setSelectedWorkOrder(wo._id);

          // filter work orders of same project
          const filtered = allWorkOrders.filter(
            (w) => String(w.project?._id || w.project) === String(projectId),
          );

          setFilteredWorkOrders(filtered);
        }
      } else {
        setFilteredWorkOrders(allWorkOrders);
      }
 }
 };

 // ================= HANDLE INPUT =================
 const handleChange = (e) => {
 const { name, value } = e.target;
 setFormData((prev) => ({ ...prev, [name]: value }));
 };

 const handleProjectChange = (e) => {
    const projectId = e.target.value;
    setSelectedProject(projectId);

    const filtered = projectId
      ? workOrders.filter(
          (wo) => String(wo.project?._id || wo.project) === String(projectId),
        )
      : workOrders;

    setFilteredWorkOrders(filtered);
    setSelectedWorkOrder("");
  };

 // ================= MINUTES =================
 const addMinute = () => setMinutes([...minutes, ""]);

 const removeMinute = (index) =>
 setMinutes(minutes.filter((_, i) => i !== index));

 const handleMinuteChange = (index, value) => {
 const updated = [...minutes];
 updated[index] = value;
 setMinutes(updated);
 };

 // ================= PARTICIPANTS =================
 const addHost = () => setHostParticipants([...hostParticipants, ""]);
 const addClient = () => setClientParticipants([...clientParticipants, ""]);

 const handleHostChange = (index, value) => {
 const updated = [...hostParticipants];
 updated[index] = value;
 setHostParticipants(updated);
 };

 const handleClientChange = (index, value) => {
 const updated = [...clientParticipants];
 updated[index] = value;
 setClientParticipants(updated);
 };

 // ================= SIGNATURE =================
 const dataURLtoBlob = (dataURL) => {
 const arr = dataURL.split(",");
 const mime = arr[0].match(/:(.*?);/)[1];
 const bstr = atob(arr[1]);
 let n = bstr.length;
 const u8arr = new Uint8Array(n);
 while (n--) u8arr[n] = bstr.charCodeAt(n);
 return new Blob([u8arr], { type: mime });
 };

 const saveClientSignature = () => {
 if (clientSigRef.current.isEmpty()) {
 return swal("Error", "Please sign before saving", "error");
 }

 const dataURL = clientSigRef.current.toDataURL("image/png");
 const blob = dataURLtoBlob(dataURL);

 const file = new File([blob], "client-signature.png", {
 type: "image/png",
 });

 setClientSignature(file);
 setShowClientModal(false);
 };

 const clearClientSignature = () => {
 clientSigRef.current.clear();
 setClientSignature(null);
 };

 // ================= SUBMIT =================
 const handleSubmit = async (e) => {
 e.preventDefault();

 if (!formData.meetingDate)
 return swal("Warning", "Meeting Date required", "warning");
 if (!formData.preparedBy)
 return swal("Warning", "Prepared By required", "warning");
 if (!selectedProject) return swal("Warning", "Select Project", "warning");
 if (!selectedWorkOrder)
 return swal("Warning", "Select Work Order", "warning");

 if (minutes.some((m) => !m.trim())) {
 return swal("Warning", "All minutes points are required", "warning");
 }

 try {
 setLoading(true);

 const formPayload = new FormData();

 Object.keys(formData).forEach((key) => {
 formPayload.append(key, formData[key]);
 });

 formPayload.append("project", selectedProject);
 formPayload.append("workOrder", selectedWorkOrder);

 formPayload.append("minutes", JSON.stringify(minutes));
 formPayload.append("hostParticipants", JSON.stringify(hostParticipants));
 formPayload.append(
 "clientParticipants",
 JSON.stringify(clientParticipants),
 );

 if (clientSignature) {
 formPayload.append("clientSignature", clientSignature);
 }

 const res = await api.post("/minutesofmeetings", formPayload, {
 headers: { "Content-Type": "multipart/form-data" },
 });

 if (res.data.success) {
 swal("Success", "MOM Created Successfully", "success");
 navigate(
 workOrderFromUrl
 ? `/mom/list?workOrder=${workOrderFromUrl}`
 : "/mom/list",
 );
 }
 } catch (err) {
 swal("Error", err.response?.data?.message || "Failed", "error");
 } finally {
 setLoading(false);
 }
 };

 // ================= UI =================
 return (
 <Fragment>
 <PageTitle activeMenu="Add MOM" motherMenu="Projects" />

 <Row>
 <Col lg={12}>
 <Card>
 <Card.Header className="d-flex justify-content-between">
 <Card.Title>Add Minutes Of Meeting</Card.Title>
 <Button
 variant="secondary"
 onClick={() =>
 navigate(
 workOrderFromUrl
 ? `/mom/list?workOrder=${workOrderFromUrl}`
 : "/mom/list",
 )
 }
 >
 <i className="fas fa-arrow-left me-2"></i>Back
 </Button>
 </Card.Header>

 <Card.Body>
 <Form onSubmit={handleSubmit}>
 {/* ================= MEETING INFO ================= */}
 <h5 className="mb-3">Meeting Details</h5>

 <Row>
 <Col md={4}>
 <Form.Group className="mb-3">
 <Form.Label>Meeting Date *</Form.Label>
 <Form.Control
 type="date"
 name="meetingDate"
 value={formData.meetingDate}
 onChange={handleChange}
 />
 </Form.Group>
 </Col>

 <Col md={4}>
 <Form.Group className="mb-3">
 <Form.Label>Prepared By *</Form.Label>
 <Form.Control
 type="text"
 name="preparedBy"
 value={formData.preparedBy}
 onChange={handleChange}
 placeholder="Enter name"
 />
 </Form.Group>
 </Col>

 <Col md={4}>
 <Form.Group className="mb-3">
 <Form.Label>Duration *</Form.Label>
 <Form.Control
 type="text"
 name="duration"
 value={formData.duration}
 onChange={handleChange}
 placeholder="e.g. 4 Hr"
 />
 </Form.Group>
 </Col>
 </Row>

 <Row>
 <Col md={4}>
 <Form.Group className="mb-3">
 <Form.Label>From Time *</Form.Label>
 <Form.Control
 type="time"
 name="fromTime"
 value={formData.fromTime}
 onChange={handleChange}
 />
 </Form.Group>
 </Col>

 <Col md={4}>
 <Form.Group className="mb-3">
 <Form.Label>To Time *</Form.Label>
 <Form.Control
 type="time"
 name="toTime"
 value={formData.toTime}
 onChange={handleChange}
 />
 </Form.Group>
 </Col>

 <Col md={4}>
 <Form.Group className="mb-3">
 <Form.Label>Venue *</Form.Label>
 <Form.Control
 type="text"
 name="venue"
 value={formData.venue}
 onChange={handleChange}
 placeholder="Enter venue"
 />
 </Form.Group>
 </Col>
 </Row>

 <Row>
 <Col md={6}>
 <Form.Group className="mb-3">
 <Form.Label>Project *</Form.Label>
 <Form.Select
 value={selectedProject}
 onChange={handleProjectChange}
 >
 <option value="">Select Project</option>
 {projects.map((project) => (
 <option key={project._id} value={project._id}>
 {project.projectName}
 </option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>

 <Col md={6}>
 <Form.Group className="mb-3">
 <Form.Label>Work Order *</Form.Label>
 <Form.Select
 value={selectedWorkOrder}
 onChange={(e) => setSelectedWorkOrder(e.target.value)}
 disabled={!selectedProject}
 >
 <option value="">Select Work Order</option>
 {filteredWorkOrders.map((wo) => (
 <option key={wo._id} value={wo._id}>
 {wo.woNumber || wo.woNo}{wo.label ? ` – ${wo.label}` : ""}
 </option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 </Row>

 {/* Attendees */}
 <h5 className="mt-4 mb-3">Attended By</h5>

 <Row>
 <Col md={6}>
 <h6>Host Participants</h6>
 {hostParticipants.map((host, index) => (
 <Form.Group key={index} className="mb-2">
 <Form.Control
 type="text"
 value={host}
 onChange={(e) =>
 handleHostChange(index, e.target.value)
 }
 placeholder={`Host ${index + 1}`}
 />
 </Form.Group>
 ))}
 <Button size="sm" variant="success" onClick={addHost}>
 + Add Host
 </Button>
 </Col>

 <Col md={6}>
 <h6>Client Participants</h6>
 {clientParticipants.map((client, index) => (
 <Form.Group key={index} className="mb-2">
 <Form.Control
 type="text"
 value={client}
 onChange={(e) =>
 handleClientChange(index, e.target.value)
 }
 placeholder={`Client ${index + 1}`}
 />
 </Form.Group>
 ))}
 <Button size="sm" variant="success" onClick={addClient}>
 + Add Client
 </Button>
 </Col>
 </Row>

 {/* Objective */}
 <h5 className="mt-4 mb-3">Objectives of Meeting</h5>
 <Form.Group>
 <Form.Control
 as="textarea"
 rows={3}
 name="objective"
 value={formData.objective}
 onChange={handleChange}
 placeholder="Enter meeting objectives"
 />
 </Form.Group>

 {/* Minutes */}
 <h5 className="mt-4 mb-3">Minutes of the Meeting</h5>

 {minutes.map((minute, index) => (
 <Card key={index} className="mb-2 p-3">
 <div className="d-flex justify-content-between">
 <strong>Point {index + 1}</strong>
 {minutes.length > 1 && (
 <Button
 size="sm"
 variant="danger"
 onClick={() => removeMinute(index)}
 >
 <i className="fas fa-trash"></i>
 </Button>
 )}
 </div>

 <Form.Control
 as="textarea"
 rows={2}
 className="mt-2"
 value={minute}
 onChange={(e) =>
 handleMinuteChange(index, e.target.value)
 }
 />
 </Card>
 ))}

 <Button variant="primary" size="sm" onClick={addMinute}>
 + Add Point
 </Button>

 {/* Signature */}
 <h5 className="mt-4 mb-3">Signature Section</h5>

 <Row>
 <Col md={6}>
 <Form.Group>
 <Form.Label>For Company</Form.Label>
 <Form.Control
 type="text"
 name="companyName"
 value={formData.companyName}
 onChange={handleChange}
 placeholder="Company Name"
 />
 </Form.Group>
 </Col>

 <Col md={6}>
 <Form.Group>
 <Form.Label>For Client</Form.Label>
 <Form.Control
 type="text"
 name="clientName"
 value={formData.clientName}
 onChange={handleChange}
 placeholder="Client Name"
 />
 </Form.Group>

 <div className="mt-2">
 <Button
 type="button"
 variant="warning"
 size="sm"
 onClick={() => setShowClientModal(true)}
 >
 Add Client Signature
 </Button>

 {clientSignature && (
 <span className="text-success ms-2"> Signed</span>
 )}
 </div>
 </Col>
 </Row>

 <div className="mt-4 d-flex gap-2">
 <Button type="submit" variant="primary" disabled={loading}>
 {loading ? "Saving..." : "Save MOM"}
 </Button>

 <Button
 variant="danger"
 onClick={() => navigate("/mom/list")}
 >
 Cancel
 </Button>
 </div>
 </Form>
 </Card.Body>
 </Card>
 </Col>
 </Row>

 {/* Signature Modal */}
 <Modal
 show={showClientModal}
 onHide={() => setShowClientModal(false)}
 centered
 >
 <Modal.Header closeButton>
 <Modal.Title>Client Signature</Modal.Title>
 </Modal.Header>

 <Modal.Body>
 <div
 style={{
 width: "100%",
 height: "200px",
 border: "2px dashed #999",
 }}
 >
 <SignatureCanvas
 ref={clientSigRef}
 penColor="black"
 canvasProps={{
 width: 500,
 height: 200,
 className: "w-100 h-100",
 }}
 />
 </div>
 </Modal.Body>

 <Modal.Footer>
 <Button variant="secondary" onClick={clearClientSignature}>
 Clear
 </Button>
 <Button variant="success" onClick={saveClientSignature}>
 Save Signature
 </Button>
 </Modal.Footer>
 </Modal>
 </Fragment>
 );
};

export default MomAdd;
